import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DocumentPreview } from "@/components/print/DocumentPreview";
import { usePrintSettings, type PrintSettings } from "@/hooks/usePrintSettings";
import { buildPrescriptionPreviewHtml } from "@/lib/print/previewHtml";
import {
  mapPrescriptionToDocumentData,
  type PrescriptionWithItems,
} from "@/lib/prescriptions/mapToDocumentData";

interface PrescriptionPrintPreviewProps {
  prescription: PrescriptionWithItems;
  className?: string;
  printSettings?: PrintSettings | null;
  /** True when the prescription is digitally signed (no manual signature line, seal space reserved). */
  signed?: boolean;
}

const buildHeaderTokensFromPrescription = (row: PrescriptionWithItems) => ({
  clinic_name: row.clinic?.name ?? undefined,
  vet_name: row.vet?.full_name ?? undefined,
  vet_crmv: row.crmv ?? undefined,
  vet_mapa: row.mapa_registration_snapshot ?? row.vet?.mapa_number ?? undefined,
  vet_phone: row.vet?.phone ?? undefined,
  vet_address: row.issuer_address ?? row.vet?.address ?? undefined,
});

export const PrescriptionPrintPreview = ({
  prescription,
  className,
  printSettings: customPrintSettings,
  signed = false,
}: PrescriptionPrintPreviewProps) => {
  const { t, i18n } = useTranslation("prescriptions");
  const { data: savedPrintSettings } = usePrintSettings();
  const lang = i18n.language === "en" ? "en" : "pt-BR";

  const html = useMemo(
    () =>
      buildPrescriptionPreviewHtml({
        printSettings: customPrintSettings ?? savedPrintSettings ?? null,
        data: mapPrescriptionToDocumentData(prescription),
        lang,
        signed,
        headerTokens: buildHeaderTokensFromPrescription(prescription),
      }),
    [customPrintSettings, savedPrintSettings, prescription, lang, signed],
  );

  return <DocumentPreview title={t("print.previewTitle")} html={html} className={className} />;
};
